// FH: Breadcrumb enhancements for the fh-breadcrumbs section.
// - On collection pages, remember the visited collection (title + URL).
// - On product pages, adjust the collection crumb based on the referrer:
//     came from home    -> show only "Home / Product"
//     came from a collection -> point the crumb back to that exact page, with the best title.
// Reads context from the nav's data attributes (data-page-type, data-root-url) so it
// doesn't depend on any global Theme object.

const STORAGE_KEY = 'lastVisitedCollection';

// Mirrors Liquid's `truncate: 40` used in sections/fh-breadcrumbs.liquid — the
// ellipsis counts toward the limit, so the two never disagree on crumb length.
const TRUNCATE_LENGTH = 40;

/**
 * Truncate a crumb label the same way Liquid's truncate filter does.
 * @param {string} text
 * @returns {string}
 */
const truncateCrumb = (text) => {
  if (text.length <= TRUNCATE_LENGTH) return text;
  return `${text.slice(0, TRUNCATE_LENGTH - 3)}...`;
};

/**
 * Store the current collection as the last visited collection.
 * @param {HTMLElement} nav
 */
const storeLastVisitedCollection = (nav) => {
  try {
    if (nav.dataset.pageType !== 'collection') return;

    const currentCrumb = nav.querySelector('.fh-breadcrumbs__link[aria-current="page"]');
    const title = currentCrumb?.textContent?.trim();
    const url = window.location.href.split('#')[0];

    if (!title || !url) return;

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ url, title, timestamp: Date.now() }));
  } catch (error) {
    // Fail silently if sessionStorage is unavailable
  }
};

/**
 * Enhance the product breadcrumb based on where the visitor came from.
 * @param {HTMLElement} nav
 */
const enhanceProductBreadcrumb = (nav) => {
  try {
    if (nav.dataset.pageType !== 'product') return;

    const list = nav.querySelector('.fh-breadcrumbs__list');
    if (!list) return;

    /** @type {HTMLAnchorElement | null} */
    let collectionLink = null;
    for (const link of list.querySelectorAll('.fh-breadcrumbs__link')) {
      if (link instanceof HTMLAnchorElement && link.href.includes('/collections/')) {
        collectionLink = link;
        break;
      }
    }

    if (!collectionLink) return;

    const referrer = document.referrer || '';
    const origin = window.location.origin || '';
    const homePath = nav.dataset.rootUrl || '/';
    const homeUrlA = origin + homePath;
    const homeUrlB = homeUrlA.replace(/\/$/, '');

    const cameFromHome =
      referrer === homeUrlA ||
      referrer === homeUrlB ||
      referrer.startsWith(`${homeUrlA}?`) ||
      referrer.startsWith(`${homeUrlB}?`) ||
      referrer.startsWith(`${homeUrlA}#`) ||
      referrer.startsWith(`${homeUrlB}#`);

    const cameFromCollection = !cameFromHome && referrer.includes('/collections/') && !referrer.includes('/products/');

    // Came from the homepage: show only "Home / Product".
    if (cameFromHome) {
      collectionLink.closest('.fh-breadcrumbs__item')?.remove();
      return;
    }

    // Didn't come from a collection: keep the Liquid-rendered breadcrumb.
    if (!cameFromCollection) return;

    const fallbackTitle = collectionLink.textContent || '';

    // 1) Prefer the freshest stored collection title (set on the collection page).
    let storedTitle = null;
    let storedFresh = false;
    try {
      const storedRaw = sessionStorage.getItem(STORAGE_KEY);
      if (storedRaw) {
        const storedData = JSON.parse(storedRaw);
        const maxAge = 60 * 60 * 1000; // 1 hour
        if (storedData?.title && storedData?.timestamp && Date.now() - storedData.timestamp < maxAge) {
          storedTitle = String(storedData.title);
          storedFresh = true;
        }
      }
    } catch (error) {
      // ignore malformed storage
    }

    // 2) Derive a handle + title from the referrer for fallback / live lookup.
    let derivedTitle = null;
    let refHandle = null;
    try {
      let refPath = referrer;
      if (origin && refPath.startsWith(origin)) refPath = refPath.slice(origin.length);

      const collectionsSplit = refPath.split('/collections/');
      if (collectionsSplit.length > 1) {
        refHandle = decodeURIComponent(collectionsSplit[1]?.split(/[/?#]/)[0] || '');
        if (refHandle) {
          derivedTitle = refHandle
            .split('-')
            .map((word) => (word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
            .join(' ');
        }
      }
    } catch (error) {
      // ignore referrer parsing issues
    }

    // Validate origin before setting href to prevent open redirects.
    // Only set href if the referrer is from the same origin.
    let safeReferrerUrl = null;
    try {
      const referrerUrl = new URL(referrer, window.location.origin);
      if (referrerUrl.origin === window.location.origin) {
        safeReferrerUrl = referrer;
      }
    } catch (error) {
      // Invalid URL; ignore
    }

    // Always point back to the exact referrer URL (filters, pagination, etc.).
    if (safeReferrerUrl) {
      collectionLink.setAttribute('href', safeReferrerUrl);
    }

    if (storedFresh && storedTitle) {
      collectionLink.textContent = truncateCrumb(storedTitle);
      return;
    }

    // Fetch the real collection title when we have a handle.
    if (refHandle) {
      fetch(`/collections/${encodeURIComponent(refHandle)}.json`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          collectionLink.textContent = truncateCrumb(
            data?.collection?.title ? String(data.collection.title) : derivedTitle || fallbackTitle
          );
        })
        .catch(() => {
          collectionLink.textContent = truncateCrumb(derivedTitle || fallbackTitle);
        });
      return;
    }

    collectionLink.textContent = truncateCrumb(derivedTitle || fallbackTitle);
  } catch (error) {
    // Fail silently to avoid breaking the page
  }
};

const initBreadcrumbs = () => {
  const nav = document.querySelector('.fh-breadcrumbs');
  if (!(nav instanceof HTMLElement)) return;
  storeLastVisitedCollection(nav);
  enhanceProductBreadcrumb(nav);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBreadcrumbs);
} else {
  initBreadcrumbs();
}
